// The "type or speak" composer shared by Translate and Room.
//
// States (data-state on the root):
//   idle      empty field + mic button
//   typing    text in the field: the mic button becomes Send
//   listening the field is replaced by a live transcript panel with a Stop button
//
// The composer only renders and reports intent. The owner decides what a submit
// or a mic press does, and calls setListening() once the mic actually opens.

import { icons } from './icons.js';

let uid = 0;
const WAVE_BARS = 22;

export function createComposer({
    placeholder = 'Type, or tap the mic',
    micLabel = 'Speak',
    sendLabel = 'Send',
    listenLabel = 'Listening',
    maxLength = 5000,
    onSubmit = () => {},
    onMicPress = () => {},
} = {}) {
    const id = `pg-composer-${++uid}`;
    const root = document.createElement('div');
    root.className = 'pg-composer';
    root.dataset.state = 'idle';
    root.innerHTML = `
        <div class="pg-composer__row">
            <div class="pg-composer__field">
                <label class="pg-sr-only" for="${id}">Message</label>
                <textarea id="${id}" class="pg-composer__input" rows="1" maxlength="${maxLength}"
                    enterkeyhint="send" autocomplete="off" placeholder=""></textarea>
                <button type="button" class="pg-composer__clear" aria-label="Clear text">${icons.x}</button>
            </div>
            <button type="button" class="pg-composer__mic">${icons.mic}</button>
            <button type="button" class="pg-composer__send">${icons.send}</button>
        </div>
        <div class="pg-composer__meta">
            <span class="pg-composer__count">0 / ${maxLength}</span>
            <span>Enter to send · Shift+Enter for a new line</span>
        </div>
        <div class="pg-composer__listen">
            <span class="pg-composer__listen-label"></span>
            <p class="pg-composer__transcript is-placeholder" aria-live="polite">Start speaking…</p>
            <div class="pg-composer__listen-row">
                <div class="pg-wave" aria-hidden="true">${'<span></span>'.repeat(WAVE_BARS)}</div>
                <button type="button" class="pg-composer__stop" aria-label="Stop listening">${icons.stop}</button>
            </div>
        </div>
        <div class="pg-composer__status" role="status"></div>
    `;

    const input = root.querySelector('.pg-composer__input');
    const clearBtn = root.querySelector('.pg-composer__clear');
    const micBtn = root.querySelector('.pg-composer__mic');
    const sendBtn = root.querySelector('.pg-composer__send');
    const stopBtn = root.querySelector('.pg-composer__stop');
    const count = root.querySelector('.pg-composer__count');
    const listenLabelEl = root.querySelector('.pg-composer__listen-label');
    const transcript = root.querySelector('.pg-composer__transcript');
    const status = root.querySelector('.pg-composer__status');
    const waveBars = Array.from(root.querySelectorAll('.pg-wave span'));

    input.placeholder = placeholder;
    micBtn.setAttribute('aria-label', micLabel);
    micBtn.title = micLabel;
    sendBtn.setAttribute('aria-label', sendLabel);
    sendBtn.title = sendLabel;
    listenLabelEl.textContent = listenLabel;

    let listening = false;
    let busy = false;
    const levels = new Array(WAVE_BARS).fill(0);

    function autosize() {
        input.style.height = 'auto';
        input.style.height = `${Math.min(input.scrollHeight, 160)}px`;
    }

    function syncState() {
        if (listening) {
            root.dataset.state = 'listening';
        } else {
            root.dataset.state = input.value.trim() ? 'typing' : 'idle';
        }
        count.textContent = `${input.value.length} / ${maxLength}`;
        sendBtn.disabled = busy;
    }

    async function submit() {
        const text = input.value.trim();
        if (!text || busy) return;
        const result = onSubmit(text);
        // Clear right away so the field is ready for the next phrase; restore on failure.
        input.value = '';
        autosize();
        syncState();
        try {
            await result;
        } catch {
            input.value = text;
            autosize();
            syncState();
        }
    }

    input.addEventListener('input', () => {
        autosize();
        syncState();
    });
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
            e.preventDefault();
            submit();
        }
    });
    clearBtn.addEventListener('click', () => {
        input.value = '';
        autosize();
        syncState();
        input.focus();
    });
    sendBtn.addEventListener('click', submit);
    micBtn.addEventListener('click', () => onMicPress());
    stopBtn.addEventListener('click', () => onMicPress());

    return {
        el: root,
        input,
        get isListening() {
            return listening;
        },
        setListening(on) {
            listening = !!on;
            if (on) {
                transcript.textContent = 'Start speaking…';
                transcript.classList.add('is-placeholder');
                levels.fill(0);
                waveBars.forEach((b) => { b.style.transform = 'scaleY(0.15)'; });
            }
            syncState();
            if (on) {
                stopBtn.focus({ preventScroll: true });
            } else if (document.activeElement === stopBtn || document.activeElement === document.body) {
                micBtn.focus({ preventScroll: true });
            }
        },
        setTranscript(text) {
            const value = (text || '').trim();
            transcript.textContent = value || 'Start speaking…';
            transcript.classList.toggle('is-placeholder', !value);
        },
        /** volume: roughly 0..0.2 from audioService. */
        setLevel(volume) {
            if (!listening) return;
            levels.shift();
            levels.push(Math.min(1, Math.max(0.12, volume * 9)));
            for (let i = 0; i < WAVE_BARS; i++) {
                waveBars[i].style.transform = `scaleY(${levels[i] || 0.12})`;
            }
        },
        setListenLabel(text) {
            listenLabelEl.textContent = text;
        },
        setMicLabel(text) {
            micBtn.setAttribute('aria-label', text);
            micBtn.title = text;
        },
        setPlaceholder(text) {
            input.placeholder = text;
        },
        setMicEnabled(enabled, reason = '') {
            micBtn.disabled = !enabled;
            micBtn.title = enabled ? micBtn.getAttribute('aria-label') : reason;
        },
        setBusy(on) {
            busy = !!on;
            syncState();
        },
        setStatus(text) {
            status.textContent = text || '';
        },
        setText(text) {
            input.value = text;
            autosize();
            syncState();
        },
        focus() {
            input.focus();
        },
    };
}
